/**
 * Button (ADR-0005: shadcn/ui pattern -- Radix primitives, source owned by us).
 *
 * Colour comes only from tokens, so every variant follows the theme switch (14.2).
 * Every size clears the 44px touch target on coarse pointers (14.3); the visually
 * smaller variants keep their hit area via padding rather than shrinking it.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium ' +
    'transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ' +
    'disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:bg-[var(--accent-hover)]',
        secondary:
          'border border-border bg-surface text-text hover:bg-surface-raised hover:border-[var(--border-strong)]',
        ghost: 'text-text-muted hover:bg-surface-raised hover:text-text',
        danger: 'bg-[var(--status-fail)] text-[var(--text-inverse)] hover:opacity-90',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        // 44px: the minimum touch target (14.3).
        md: 'h-11 px-4',
        sm: 'h-9 px-3 text-sm',
        // Icon-only buttons stay square and full height so they remain tappable.
        icon: 'h-11 w-11 shrink-0',
        iconSm: 'h-9 w-9 shrink-0',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render as the child element (a router `Link`, say) instead of a `<button>`. */
  asChild?: boolean
  /** Shows a spinner and disables the button. */
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
  ref,
) {
  // Radix `Slot` merges props onto exactly one child, so `asChild` must render a
  // single element. Emitting the spinner alongside it gives Slot two children and
  // throws -- which took down every page using `<Button asChild><Link/></Button>`,
  // and did it inside the error boundary where the cause was invisible.
  if (asChild) {
    return (
      <Slot
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        aria-busy={loading || undefined}
        {...props}
      >
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      // Tells assistive tech the control is working, not broken.
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  )
})

export { buttonVariants }
